import type { IamGroupType, UserGroupCarRole, UserGroupListApiResponse } from '@navio/nsdk';
import { URLKeys } from 'noddi-async';
import { type FilterOption, type FilterOptionSource, usePagination } from 'noddi-ui-web';
import { getCountsByKey, getUserGroupTypeIcon, resolveUserGroupType, useDebounceValue } from 'noddi-util';
import { customerInfoName, customerInfoSearchLabel } from '../../../features/UserGroups/customerInfo';
import { buildUserGroupSelectOption } from '../../../features/UserGroups/userGroupSelectOption';
import { toFilterOptionSource, useFilterOptionsGet } from '../useFilterOptionsGet';

type UserGroupOptionsArgs = {
  serviceDepartmentIds?: number[];
  serviceOrganizationIds?: number[];
  types?: IamGroupType[];
  /** Current car roles. Leaseholder options pass leaseholder only. */
  carRoles?: UserGroupCarRole[];
  /** Only groups referenced as an invoice-to payer. */
  isInvoicePayer?: boolean;
  /** Skip the option request until the filter is shown. */
  enabled?: boolean;
};

/** Customers (user groups) as filter options, searchable, with contact details under the name. For `unit.multiSelect`. */
export const useUserGroupOptions = ({
  serviceDepartmentIds,
  serviceOrganizationIds,
  types,
  carRoles,
  isInvoicePayer,
  enabled = true
}: UserGroupOptionsArgs = {}): FilterOptionSource<number> => {
  const { pageIndex, pageSize, search, setSearch } = usePagination();
  const debouncedSearch = useDebounceValue({ value: search, delay: 300 });

  const query = useFilterOptionsGet({
    type: URLKeys.getPaginatedUserGroups,
    input: {
      pageIndex,
      pageSize,
      search: debouncedSearch,
      serviceDepartmentIds,
      serviceOrganizationIds,
      types,
      carRoles,
      isInvoicePayer
    },
    queryConfig: { enabled, select: toUserGroupOptions }
  });

  return toFilterOptionSource(query, 'number', { search, onSearch: setSearch });
};

const toUserGroupOptions = ({ results }: UserGroupListApiResponse): FilterOption<number>[] => {
  const nameCounts = getCountsByKey(results, (userGroup) => customerInfoName(userGroup));
  return results.map((userGroup) => {
    const option = buildUserGroupSelectOption(userGroup);
    const isDuplicate = (nameCounts.get(customerInfoName(userGroup)) ?? 0) > 1;
    return {
      ...option,
      label: isDuplicate ? customerInfoSearchLabel(userGroup) : option.label,
      icon: getUserGroupTypeIcon(resolveUserGroupType(userGroup))
    };
  });
};
